import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { loadScript } from "@/lib/videoDuration";

/** Link de player com a API do YouTube/Vimeo ligada e sem tela cheia interna
 *  (a tela cheia é a nossa, para a marca d'água continuar visível). */
export function playerSrc(embed, autoplay = false, cc = "", inicio = 0) {
  if (!embed) return "";
  const ap = autoplay ? "&autoplay=1" : "";
  const on = cc && cc !== "off";
  if (embed.includes("youtube.com/embed/")) {
    const sep = embed.includes("?") ? "&" : "?";
    const legenda = on ? `&cc_load_policy=1&cc_lang_pref=${cc}&hl=${cc}` : "";
    return `${embed}${sep}enablejsapi=1&fs=0&rel=0&modestbranding=1&playsinline=1${ap}${legenda}${inicio ? `&start=${inicio}` : ""}&origin=${encodeURIComponent(window.location.origin)}`;
  }
  if (embed.includes("player.vimeo.com")) {
    const sep = embed.includes("?") ? "&" : "?";
    return `${embed}${sep}dnt=1&playsinline=1${ap}${on ? `&texttrack=${cc}` : ""}${inicio ? `#t=${inicio}s` : ""}`;
  }
  return embed;
}

/**
 * Player de vídeo com:
 *  - marca d'água (nome, CPF e IP do aluno) que muda de lugar;
 *  - API para tempo atual, pular para um minuto, velocidade e aviso de fim do vídeo.
 */
export const VideoStage = forwardRef(function VideoStage({ src, title, watermark, captions = "", native = false, autoPlay = false, onEnded, onPlay, onPause, onTime, overlay }, ref) {
  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const apiRef = useRef({ kind: "", player: null });
  // callbacks sempre atualizados (evita usar versões antigas dentro dos eventos do player)
  const cb = useRef({});
  cb.current = { onEnded, onPlay, onPause, onTime };
  const [pos, setPos] = useState({ top: 12, left: 8 });
  const ccRef = useRef(captions);
  ccRef.current = captions;

  // liga/desliga a legenda no idioma escolhido (usa a legenda do vídeo ou a automática/traduzida)
  const applyCaptions = async () => {
    const { kind, player, ready } = apiRef.current;
    const cc = ccRef.current;
    if (!player || !cc) return;
    try {
      if (kind === "yt" && ready) {
        if (cc === "off") { player.unloadModule?.("captions"); player.unloadModule?.("cc"); return; }
        player.loadModule?.("captions");
        player.setOption?.("captions", "track", { languageCode: cc });
      }
      if (kind === "vimeo") {
        if (cc === "off") { await player.disableTextTrack(); return; }
        const tracks = await player.getTextTracks();
        const t = tracks.find((x) => x.language.toLowerCase().startsWith(cc)) || tracks[0];
        if (t) await player.enableTextTrack(t.language, t.kind);
      }
    } catch (e) { /* vídeo sem legenda disponível */ }
  };

  // liga a API do player
  useEffect(() => {
    apiRef.current = { kind: "", player: null };
    if (native) { apiRef.current = { kind: "native", player: videoRef.current }; return undefined; }
    const el = iframeRef.current;
    if (!el || !src) return;
    let cancelled = false;
    (async () => {
      try {
        if (src.includes("youtube.com")) {
          await loadScript("https://www.youtube.com/iframe_api", () => window.YT && window.YT.Player);
          if (cancelled) return;
          let poll = null;
          const p = new window.YT.Player(el, {
            events: {
              onReady: () => { apiRef.current.ready = true; setTimeout(applyCaptions, 300); },
              onStateChange: (e) => {
                if (e.data === 1) {
                  if (!apiRef.current.ccDone) { apiRef.current.ccDone = true; applyCaptions(); }
                  cb.current.onPlay?.();
                  clearInterval(poll);
                  poll = setInterval(() => { try { cb.current.onTime?.(p.getCurrentTime()); } catch (x) {} }, 1000);
                } else {
                  clearInterval(poll);
                  if (e.data === 2) cb.current.onPause?.();
                  if (e.data === 0) cb.current.onEnded?.();
                }
              },
            },
          });
          apiRef.current = { kind: "yt", player: p, stop: () => clearInterval(poll), ready: false };
        } else if (src.includes("vimeo.com")) {
          await loadScript("https://player.vimeo.com/api/player.js", () => window.Vimeo && window.Vimeo.Player);
          if (cancelled) return;
          const p = new window.Vimeo.Player(el);
          let ending = false;
          p.on("play", () => { ending = false; cb.current.onPlay?.(); });
          p.on("pause", () => { if (!ending) cb.current.onPause?.(); });
          p.on("timeupdate", (d) => cb.current.onTime?.(d.seconds));
          p.on("ended", () => { ending = true; cb.current.onEnded?.(); });
          apiRef.current = { kind: "vimeo", player: p };
          p.ready().then(applyCaptions).catch(() => {});
        }
      } catch (e) { /* sem API: o vídeo toca normalmente, só sem os recursos extras */ }
    })();
    return () => { cancelled = true; apiRef.current.stop?.(); };
  }, [src, native]); // eslint-disable-line react-hooks/exhaustive-deps

  // aluno trocou o idioma da legenda
  useEffect(() => { applyCaptions(); }, [captions]); // eslint-disable-line react-hooks/exhaustive-deps

  // marca d'água muda de lugar a cada 7 s
  useEffect(() => {
    const t = setInterval(() => setPos({ top: 6 + Math.random() * 78, left: 4 + Math.random() * 62 }), 7000);
    return () => clearInterval(t);
  }, []);

  useImperativeHandle(ref, () => ({
    async currentTime() {
      const { kind, player } = apiRef.current;
      try {
        if (kind === "native") return Math.floor(player.currentTime || 0);
        if (kind === "yt") return Math.floor(player.getCurrentTime?.() || 0);
        if (kind === "vimeo") return Math.floor(await player.getCurrentTime());
      } catch (e) {}
      return 0;
    },
    async seek(sec) {
      const { kind, player } = apiRef.current;
      try {
        if (kind === "native") { player.currentTime = sec; player.play().catch(() => {}); return true; }
        if (kind === "yt") { player.seekTo(sec, true); player.playVideo?.(); return true; }
        if (kind === "vimeo") { await player.setCurrentTime(sec); await player.play(); return true; }
      } catch (e) {}
      return false;
    },
    async play() {
      const { kind, player } = apiRef.current;
      try { if (kind === "native") await player.play(); if (kind === "yt") player.playVideo(); if (kind === "vimeo") await player.play(); } catch (e) {}
    },
    async setRate(r) {
      const { kind, player } = apiRef.current;
      try {
        if (kind === "native") { player.playbackRate = r; return true; }
        if (kind === "yt") { player.setPlaybackRate(r); return true; }
        if (kind === "vimeo") { await player.setPlaybackRate(r); return true; }
      } catch (e) {}
      return false;
    },
  }), []);

  return (
    <div className="relative w-full h-full bg-black">
      {src && native ? (
        // vídeo hospedado na plataforma: player do próprio navegador, sem download nem "imagem no canto" (a marca d'água sairia da tela)
        <video ref={videoRef} key={src} src={src} title={title} className="w-full h-full bg-black" controls playsInline autoPlay={autoPlay}
          preload="metadata" controlsList="nodownload nofullscreen noremoteplayback" disablePictureInPicture disableRemotePlayback
          onContextMenu={(e) => e.preventDefault()}
          onPlay={() => cb.current.onPlay?.()}
          onPause={(e) => { if (!e.currentTarget.ended) cb.current.onPause?.(); }}
          onTimeUpdate={(e) => cb.current.onTime?.(e.currentTarget.currentTime)}
          onEnded={() => cb.current.onEnded?.()}
          data-testid="native-video" />
      ) : src ? (
        <iframe ref={iframeRef} key={src} src={src} title={title} className="w-full h-full"
          allow="autoplay; encrypted-media; picture-in-picture; accelerometer; gyroscope" referrerPolicy="strict-origin-when-cross-origin" />
      ) : null}
      {watermark && (
        <div className="pointer-events-none absolute z-10 text-[11px] sm:text-xs font-mono leading-tight select-none transition-all duration-1000"
          style={{ top: `${pos.top}%`, left: `${pos.left}%`, color: "rgba(74,222,128,.55)", textShadow: "0 0 2px rgba(0,0,0,.6)" }}
          data-testid="video-watermark">
          <div>{watermark.name}</div>
          {watermark.document && <div>{watermark.document}</div>}
          <div>{watermark.ip}</div>
        </div>
      )}
      {overlay}
    </div>
  );
});
